import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { Download, type FolderOpen, X } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Button,
  buttonVariants,
  Card,
  Combobox,
  type ComboboxOption,
  DropdownMenu,
  DropdownMenuChevron,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FilterToggle,
  MultiSelect,
  Pagination,
  Skeleton,
  SortableColumnHeader,
  StatCard,
  StatusDot,
  useToast,
} from "@rumbledirect/ui-v2";
import {
  ROW_GRID,
  type DossierStatusMeta,
  getDossierColumns,
  getRowGridStyle,
} from "@/dossiers/dossierColumns";
import { COMPANY_TYPE_LABELS } from "@/dossiers/companyLabels";
import { DOSSIER_KPI_TILE_META } from "@/dossiers/dossierKpiTiles";
import {
  CLAIMABLE_OPTIONS,
  EXTERNAL_INSURED_OPTIONS,
} from "@/dossiers/dossierReportFilters";
import { toEnumLabelMap } from "@/reporting/reportingCharts";
import { DOSSIER_TYPE_OPTIONS } from "@/dossiers/dossierTypeLabels";
import { REPAIR_TYPE_OPTIONS } from "@/dossiers/repairTypeLabels";
import {
  ACCIDENT_REPORT_IMPORT_PERMISSION,
  DOSSIER_CREATE_PERMISSION,
} from "@/dossiers/dossierPermissions";
import { AccidentReportImportDialog } from "@/dossiers/AccidentReportImportDialog";
import type { DossiersSearch, Period } from "@/dossiers/dossiersSearch";
import { coerceSortBy, coerceSortDirection } from "@/dossiers/dossiersSearch";
import {
  END_OF_CONTRACT_LABEL,
  END_OF_CONTRACT_OPTIONS,
  type EndOfContract,
  toEndOfContract,
  toEndOfContractParams,
} from "@/dossiers/endOfContractFilter";
import {
  type DossierOverviewKpi,
  type DossierOverviewSorting,
  type DossierSortDirection,
  type DossierSortField,
  resolveDefaultDossierOverviewLayout,
} from "@/dossiers/dossierOverviewLayout";
import { useDossierOverviewLayout } from "@/dossiers/useDossierOverviewLayout";
import { DossierOverviewSkeleton } from "@/dossiers/DossierOverviewSkeleton";
import { DossiersRows } from "@/dossiers/DossiersRows";
import { useExportDossiers } from "@/dossiers/useExportDossiers";
import { useDossierStats } from "@/dossiers/useDossierStats";
import { useDossierStatusLookup } from "@/dossiers/dossierStatusLookup";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { useGetAdminCompaniesBrief } from "@/lib/api/generated/company/company";
import {
  toNonEmptyString,
  toStringList,
  toThreeStateBoolean,
} from "@/lib/searchParams";
import {
  useGetAdminDossiers,
  useGetDossiersCauseOfDamage,
} from "@/lib/api/generated/dossier/dossier";
import { useGetAdminServicePartnersBrief } from "@/lib/api/generated/service-partner/service-partner";
import { useGetAdminUsersBrief } from "@/lib/api/generated/user/user";
import { useTeamOptions } from "@/teams/useTeamOptions";
import { useGetAdminVehicleBrands } from "@/lib/api/generated/vehicle/vehicle";
import { cn } from "@rumbledirect/ui-v2/cn";
import {
  booleanToOptionValue,
  optionValueToBoolean,
} from "@/lib/threeStateBoolean";
import { toVehicleBrandOptions } from "@/vehicles/vehicleBrandOptions";
import {
  AnimatedNumber,
  staggerContainer,
  staggerItem,
  useMotionInitial,
} from "@rumbledirect/ui-v2/motion";

/**
 * Backend RBAC permission gating the "Nieuw dossier" entry point, matching
 * the admin-scoped create-dossier endpoint (`POST
 * /affiliates/{affiliateId}/admin/dossiers/repair-direct/generic`, see the
 * RK-794 backend PR).
 */

/**
 * Relative period presets for the "created" date filter. The union only
 * covers fixed presets today; a future custom date-range picker slice would
 * add a `"custom"` case here and extend `periodToCreatedAfter` to read
 * explicit `createdAfter`/`createdBefore` search params for that case
 * instead of computing them — no other call site would need to change.
 */
const PERIOD_OPTIONS: readonly ComboboxOption[] = [
  { value: "7", label: "Laatste 7 dagen" },
  { value: "30", label: "Laatste 30 dagen" },
  { value: "90", label: "Laatste 90 dagen" },
];

/**
 * The direction a column starts in the moment it becomes the active sort,
 * having previously been inactive: ascending for a text column, descending
 * for a date (so selecting "Aangemaakt" reads as "newest first" immediately —
 * this is the shipped default's own default direction, though an affiliate's
 * configured `defaultDirection` may differ, as broekhuis-lease's does).
 */
const DEFAULT_DIRECTION_FOR_FIELD: Record<
  DossierSortField,
  DossierSortDirection
> = {
  driver: "ASC",
  createdAt: "DESC",
};

/**
 * The direction a header click on `clickedField` results in: reverses the
 * current direction when `clickedField` is already the active column (one
 * active column, no third "unsorted" state to cycle through), otherwise
 * starts `clickedField` at its own default direction. Shared between the
 * click handler (`toggleSort`) and the header label computation below, so
 * the accessible label describing "what clicking does next" can never drift
 * from what clicking actually does.
 */
function nextSortDirection(
  activeField: DossierSortField | undefined,
  activeDirection: DossierSortDirection | undefined,
  clickedField: DossierSortField,
): DossierSortDirection {
  if (activeField === clickedField && activeDirection !== undefined) {
    return activeDirection === "ASC" ? "DESC" : "ASC";
  }
  return DEFAULT_DIRECTION_FOR_FIELD[clickedField];
}

/**
 * The sort actually applied: the URL's choice only if the affiliate's layout
 * still allows sorting by it, otherwise the layout's own default (or no sort
 * at all when the affiliate configured none). Resolved as one pair, not two
 * independent `??` fallbacks — a URL naming a field but no direction takes
 * that field's own default direction (`DEFAULT_DIRECTION_FOR_FIELD`), never
 * the layout's unrelated `defaultDirection`, which belongs to
 * `defaultColumn` alone.
 */
function resolveEffectiveSort(
  sortBy: DossierSortField | undefined,
  sortDirection: DossierSortDirection | undefined,
  sorting: DossierOverviewSorting | undefined,
): {
  readonly sortBy: DossierSortField | undefined;
  readonly sortDirection: DossierSortDirection | undefined;
} {
  if (sortBy !== undefined && sorting?.columns.includes(sortBy)) {
    return {
      sortBy,
      sortDirection: sortDirection ?? DEFAULT_DIRECTION_FOR_FIELD[sortBy],
    };
  }
  return {
    sortBy: sorting?.defaultColumn,
    sortDirection: sorting?.defaultDirection,
  };
}

/**
 * A selectable Status filter chip, derived from /admin/dossier-statuses/brief.
 * `names` holds every backend status name that shares this chip's label (e.g.
 * "Aangemaakt bij hersteller" exists as both `check_aangemaakt_bij_hersteller`
 * and `gw_aangemaakt_bij_hersteller` — two workflow-specific states with
 * identical display text) — see the merge in `statusOptions` below.
 */
interface StatusOption {
  readonly names: readonly string[];
  readonly label: string;
  readonly color: DossierStatusMeta["color"];
}

/**
 * Dutch labels for the Opdrachtgever Combobox's per-option hint, distinguishing
 * an insurer from a lease company in the merged /admin/companies/brief list.
 * Shared with the create-dossier form's Opdrachtgever field — see
 * `dossiers/companyLabels.ts`.
 */

/** Milliseconds in a day, used to derive a period's cutoff below. */
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Derives the `createdAt[after]` bound for a relative period preset,
 * `undefined` for `"all"` (no bound). Computed from the *relative* period
 * (rather than storing an absolute date in the URL) so a shared/bookmarked
 * "last 30 days" link keeps meaning "the last 30 days" whenever it's opened,
 * rather than freezing a date that quickly goes stale. The cutoff is
 * computed via millisecond arithmetic rather than `Date#setDate`, which is
 * local-time and DST sensitive: around a DST transition, subtracting days
 * through `setDate`/`getDate` can shift the cutoff by an hour, so a true
 * "last N x24 hours" period must be derived from milliseconds instead.
 */
function periodToCreatedAfter(period: Period): string | undefined {
  if (period === "all") {
    return undefined;
  }
  const days = Number(period);
  return new Date(Date.now() - days * DAY_IN_MS).toISOString();
}

export const Route = createFileRoute("/_authenticated/dossiers")({
  // Pagination and filters live in the URL so a view is shareable and survives
  // reloads.
  validateSearch: (search: Record<string, unknown>): DossiersSearch => {
    const page = Number(search.page);
    // TanStack Router's default search codec parses raw, numeric-looking URL
    // query values (e.g. a freshly-loaded `?period=30`) into actual numbers,
    // while values written programmatically via `navigate()` stay as the
    // literal string this route always writes — so both forms must be
    // accepted here, exactly like `page` above.
    const rawPeriod = String(search.period ?? "");
    const period =
      rawPeriod === "7" || rawPeriod === "30" || rawPeriod === "90"
        ? (rawPeriod as Period)
        : "all";
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      archived: search.archived === true,
      unread: search.unread === true,
      actionRequired: search.actionRequired === true,
      period,
      servicePartnerId: toNonEmptyString(search.servicePartnerId),
      companyId: toNonEmptyString(search.companyId),
      assigneeId: toNonEmptyString(search.assigneeId),
      userCollectionId: toNonEmptyString(search.userCollectionId),
      // Unlike the scalar params above, a list parameter reaches the
      // validator in several shapes; `toStringList` reconciles them and
      // explains why.
      status: toStringList(search.status),
      // Three-state, unlike the strict two-state booleans above: absent and
      // malformed both mean "no filter", and `false` is a real choice.
      externalInsured: toThreeStateBoolean(search.externalInsured),
      claimable: toThreeStateBoolean(search.claimable),
      brand: toStringList(search.brand),
      endOfContract: toEndOfContract(search.endOfContract),
      causeOfDamage: toNonEmptyString(search.causeOfDamage),
      dossierType: toNonEmptyString(search.dossierType),
      repairType: toNonEmptyString(search.repairType),
      // Undefined (rather than defaulted here, unlike every filter above) is
      // itself a meaningful value: "no explicit choice", resolved against the
      // affiliate's configured default once the layout has loaded (see
      // `effectiveSort` below) — something this pure, layout-unaware function
      // cannot do.
      sortBy: coerceSortBy(search.sortBy),
      sortDirection: coerceSortDirection(search.sortDirection),
    };
  },
  component: DossiersPage,
});

function DossiersPage(): ReactNode {
  const motionInitial = useMotionInitial("hidden");
  const {
    page,
    archived,
    unread,
    actionRequired,
    period,
    servicePartnerId,
    companyId,
    assigneeId,
    userCollectionId,
    status,
    externalInsured,
    claimable,
    brand,
    endOfContract,
    causeOfDamage,
    dossierType,
    repairType,
    sortBy,
    sortDirection,
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const currentUser = useCurrentUser();
  const toast = useToast();
  const mayCreateDossier = hasPermission(
    currentUser,
    DOSSIER_CREATE_PERMISSION,
  );
  const mayImportAccidentReports = hasPermission(
    currentUser,
    ACCIDENT_REPORT_IMPORT_PERMISSION,
  );
  const [accidentReportImportOpen, setAccidentReportImportOpen] =
    useState(false);
  const exportMutation = useExportDossiers();

  // Frozen per period value: recomputing the millisecond-precise cutoff on
  // every render yields a new `createdAt[after]` — and thus a new React Query
  // key — on each re-render, so the query resolving would re-render the page
  // into another fetch, endlessly. The cutoff stays relative on every page
  // load and filter change; it just doesn't drift mid-session.
  const createdAfter = useMemo(() => periodToCreatedAfter(period), [period]);

  // Which KPI tiles, filters and columns this affiliate shows, and how many
  // dossiers fit on a page. Resolved server-side for the current user, so
  // anything they may not see never arrives here (see useDossierOverviewLayout).
  const layoutQuery = useDossierOverviewLayout();
  // The default stands in only for what is computed before the real
  // configuration is known (the page size the disabled list query would use,
  // and the fallback the hook resolves a 404 to). Nothing derived from it is
  // ever rendered: the page shows `DossierOverviewSkeleton` while the
  // configuration loads and its error state when it fails, so no tile, filter
  // or column the affiliate did not configure is painted in either case.
  const layout =
    layoutQuery.data ?? resolveDefaultDossierOverviewLayout(currentUser);
  // The configuration is only known once it has actually arrived — a failed
  // query is not pending either, and it carries no data.
  const isLayoutResolved = layoutQuery.data !== undefined;
  // A configuration whose columns are all permission-gated legitimately
  // resolves to none for this user. That is a server decision, not a broken
  // payload, so the list is replaced by a notice instead of rendering a header
  // and rows with no cells in them.
  const hasNoColumns = layout.columns.length === 0;
  const itemsPerPage = layout.itemsPerPage;
  const enabledFilters = useMemo(
    () => new Set(layout.filters),
    [layout.filters],
  );

  // The URL carries "no explicit choice" as `undefined` (see the doc on
  // `DossiersSearch.sortBy`); the affiliate's configured default fills that
  // in once the layout has loaded, and a URL choice the layout no longer
  // allows (a stale link, a different affiliate) is treated the same as no
  // choice. Both stay `undefined` when the affiliate configured no sorting
  // at all (`layout.sorting` absent), which is what keeps every header
  // non-clickable and sends no `order[...]` parameter below — sorting is
  // opt-in per affiliate, not a default every list gets.
  const { sortBy: effectiveSortBy, sortDirection: effectiveSortDirection } =
    resolveEffectiveSort(sortBy, sortDirection, layout.sorting);

  // Shared with the export POST so the queued job always sees the same
  // filters the list is showing. Pagination rides along too: the worker
  // ignores it, and sending a second, stripped object is how the two would
  // drift.
  const listParams = useMemo(() => {
    const { matchedRule, excludeMatchedRule } = toEndOfContractParams(
      endOfContract,
      layout.filterOptions?.matchedRule?.ruleIds ?? [],
    );
    return {
      page,
      itemsPerPage,
      "exists[archivedAt]": archived,
      ...(unread ? { "exists[adminFirstReadAt]": false } : {}),
      ...(actionRequired ? { actionRequired: true } : {}),
      ...(createdAfter ? { "createdAt[after]": createdAfter } : {}),
      ...(servicePartnerId ? { servicePartnerId } : {}),
      ...(companyId ? { companyId } : {}),
      ...(assigneeId ? { assigneeId } : {}),
      ...(userCollectionId ? { userCollectionId } : {}),
      // The generated param is named `status[]` (not `status`): the
      // backend's status filter uses API Platform's built-in ExactFilter,
      // which documents/accepts only the bracketed array form (see
      // DossierResource.php).
      ...(status.length > 0 ? { "status[]": [...status] } : {}),
      // Three-state: `false` is a deliberate choice and must be sent; only
      // `undefined` (no choice) omits the parameter. A truthiness check here
      // would silently drop `false`, turning "Intern verzekerd" into "Alle".
      ...(externalInsured !== undefined ? { externalInsured } : {}),
      ...(claimable !== undefined ? { claimable } : {}),
      // Same bracketed-array form as `status[]` above (see its comment).
      ...(brand.length > 0 ? { "brand[]": [...brand] } : {}),
      ...(matchedRule ? { "matchedRule[]": matchedRule } : {}),
      // The generated key has no brackets; axios adds them when serializing.
      ...(excludeMatchedRule ? { excludeMatchedRule } : {}),
      ...(causeOfDamage ? { causeOfDamage } : {}),
      ...(dossierType ? { dossierType } : {}),
      ...(repairType ? { repairType } : {}),
      // Sorts the entire filtered collection server-side, before pagination
      // (never just the current page): omitted entirely when the affiliate
      // configured no sorting, so the collection keeps its unconfigured
      // order. `order[id]` is always appended alongside the chosen column —
      // a fixed, deterministic tie-break so two dossiers equal on that
      // column keep a stable relative order across pages (the backend applies
      // `id` last in its order filter). Written as two
      // disjoint literal-keyed branches (one per sortable field) rather
      // than a single computed `[DOSSIER_SORT_PARAM[field]]: ...` property:
      // the generated params type gives each `order[...]` key its own
      // literal enum, and a dynamically computed key widens to a generic
      // index signature that no longer satisfies it.
      ...(effectiveSortBy === "driver" && effectiveSortDirection
        ? {
            "order[driver.person.lastName]": effectiveSortDirection,
            "order[id]": "DESC" as const,
          }
        : {}),
      ...(effectiveSortBy === "createdAt" && effectiveSortDirection
        ? {
            "order[createdAt]": effectiveSortDirection,
            "order[id]": "DESC" as const,
          }
        : {}),
    };
  }, [
    page,
    itemsPerPage,
    archived,
    unread,
    actionRequired,
    createdAfter,
    servicePartnerId,
    companyId,
    assigneeId,
    userCollectionId,
    status,
    externalInsured,
    claimable,
    brand,
    endOfContract,
    layout.filterOptions,
    causeOfDamage,
    dossierType,
    repairType,
    effectiveSortBy,
    effectiveSortDirection,
  ]);

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminDossiers(
      listParams,
      // Keeps the previous page's rows on screen while the next page/filter is
      // fetching (isPlaceholderData=true), instead of dropping back to
      // skeletons on every pagination click. See the stagger container below
      // for how the entrance animation is kept in sync with this.
      {
        query: {
          placeholderData: keepPreviousData,
          // Held back until the configured page size is known, so the list is
          // not fetched twice when the affiliate configured something other
          // than the default. The page shows `DossierOverviewSkeleton` while
          // the configuration loads, and a disabled query still reports
          // `isPending`, so the list keeps its own skeleton right after.
          // A configuration that resolves to no column renders no list at all,
          // so its rows are not worth fetching either.
          enabled: isLayoutResolved && !hasNoColumns,
        },
      },
    );

  // A configuration that failed to load fails the whole page rather than
  // rendering one silently built from defaults the affiliate never chose: the
  // list, its filters and its tiles are all replaced by the same recoverable
  // error state (a missing configuration is a 404 and already resolved to the
  // default by the hook, so it never lands here). Retrying recovers both
  // queries at once.
  const retryList = (): void => {
    if (layoutQuery.isError) {
      // The list query is disabled until the configuration lands and fetches
      // itself once it does, so retrying it here would only fire a request
      // with the fallback page size the affiliate may not have configured.
      void layoutQuery.refetch();
      return;
    }

    void refetch();
  };

  // Single lightweight KPI-counts query so the tiles show stable counts of
  // the active scope, independent of the list's current filter — mirroring
  // the Ledger concept, where the tiles are always-available one-tap
  // shortcuts rather than a readout of the current query.
  const statsQuery = useDossierStats();
  const isStatsPending = statsQuery.isPending || statsQuery.isError;

  // Brief (non-paginated) list of service partners backing the Hersteller
  // filter's Combobox options. Fetched separately from the dossiers list
  // since it never changes with pagination/other filters. Entries without an
  // id are skipped rather than coerced to an empty-string value: the Combobox
  // uses `value` as its React key (a shared "" key would collide across
  // multiple such entries) and as the selection value, and an empty-string
  // `servicePartnerId` would make `filtersActive` true while the request
  // spread (which checks truthiness) sends no filter at all.
  const servicePartnersQuery = useGetAdminServicePartnersBrief(undefined, {
    // A filter the affiliate did not configure is never rendered, so fetching
    // the options that would fill it is pure waste. Held back until the
    // configuration is known, so a filter that turns out to be hidden never
    // fetches them on the strength of the placeholder default.
    query: {
      enabled: isLayoutResolved && enabledFilters.has("servicePartner"),
    },
  });
  const servicePartnerOptions: ComboboxOption[] = useMemo(
    () =>
      (servicePartnersQuery.data?.member ?? []).flatMap((servicePartner) =>
        servicePartner.id
          ? [
              {
                value: servicePartner.id,
                label: servicePartner.name,
                hint: servicePartner.city ?? undefined,
              },
            ]
          : [],
      ),
    [servicePartnersQuery.data],
  );

  // Brief (non-paginated) list of insurers and lease companies backing the
  // Opdrachtgever filter's Combobox options — same defensive id-skip and
  // separate-query rationale as the Hersteller options above. The hint shows
  // the company's type (Verzekeraar/Leasemaatschappij) rather than its city:
  // in a merged insurer+lease list, distinguishing the two is more useful
  // than location.
  const companiesQuery = useGetAdminCompaniesBrief(undefined, {
    query: { enabled: isLayoutResolved && enabledFilters.has("company") },
  });
  const companyOptions: ComboboxOption[] = useMemo(
    () =>
      (companiesQuery.data?.member ?? []).flatMap((company) =>
        company.id
          ? [
              {
                value: company.id,
                label: company.name,
                hint: COMPANY_TYPE_LABELS[company.type],
              },
            ]
          : [],
      ),
    [companiesQuery.data],
  );

  // Brief (non-paginated) list of admin users (behandelaars) backing the
  // Toegewezen aan filter's Combobox options — same defensive id-skip
  // rationale as the other brief options above. The current user's own
  // option is suffixed " (ik)" (mirroring the Ledger concept), so they can
  // spot "assigned to me" at a glance without a separate lookup.
  const usersQuery = useGetAdminUsersBrief({
    // Same as the Hersteller options above: not fetched for a filter this
    // affiliate did not configure.
    query: {
      enabled: isLayoutResolved && enabledFilters.has("assignee"),
    },
  });
  const assigneeOptions: ComboboxOption[] = useMemo(
    () =>
      (usersQuery.data?.member ?? []).flatMap((user) =>
        user.id
          ? [
              {
                value: user.id,
                label:
                  user.id === currentUser.userId
                    ? `${user.fullName} (ik)`
                    : user.fullName,
              },
            ]
          : [],
      ),
    [usersQuery.data, currentUser.userId],
  );

  // The teams backing the Team filter, every page of them.
  const teamsQuery = useTeamOptions({
    enabled: isLayoutResolved && enabledFilters.has("userCollection"),
  });
  const teamOptions = teamsQuery.data ?? [];

  // Unpaginated vehicle-brand reference list backing the Merk filter's
  // MultiSelect options — same separate-query and filter-gating rationale as
  // the brief options above. The generated hook's query key is kept as-is
  // (not overridden): the tenant switch drops the whole cache via the auth
  // bridge, the same guarantee every other option query in this bar relies
  // on.
  const vehicleBrandsQuery = useGetAdminVehicleBrands({
    query: {
      enabled: isLayoutResolved && enabledFilters.has("brand"),
    },
  });
  // The CauseOfDamage enum collection backing the Schadeoorzaak filter. Public
  // reference data, so it is only fetched once the filter is enabled.
  const causesOfDamageQuery = useGetDossiersCauseOfDamage({
    query: {
      enabled: isLayoutResolved && enabledFilters.has("causeOfDamage"),
    },
  });
  const causeOfDamageOptions: ComboboxOption[] = useMemo(
    () =>
      Object.entries(toEnumLabelMap(causesOfDamageQuery.data))
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, "nl")),
    [causesOfDamageQuery.data],
  );
  const brandOptions: ComboboxOption[] = useMemo(
    () => toVehicleBrandOptions(vehicleBrandsQuery.data?.member ?? []),
    [vehicleBrandsQuery.data],
  );

  // Brief (non-paginated) list of dossier statuses, grouped by dossier
  // subtype workflow (see /admin/dossier-statuses/brief), backing the
  // Status filter chips and the Status column's label/tone lookup.
  // Flattened and deduplicated by status name here (first-seen wins, in the
  // order the groups/statuses come back): a status shared across dossier
  // subtypes' workflows only needs one chip/lookup entry for this flat
  // filter/column. The grouping itself stays meaningful in the API response
  // for a future per-subtype kanban board — it's just not needed here.
  const {
    lookup: statusLookup,
    isPending: statusesPending,
    isError: statusesErrored,
    refetch: refetchStatuses,
  } = useDossierStatusLookup();
  const statusOptions: readonly StatusOption[] = useMemo(() => {
    // Merge by label, not just by name: two workflow-specific states (e.g.
    // Check's and Greenwheels' own "Aangemaakt bij hersteller" states) can
    // share identical display text, and should render as a single chip that
    // toggles both underlying names together rather than two visually
    // identical chips.
    const byLabel = new Map<
      string,
      { names: string[]; color: DossierStatusMeta["color"] }
    >();
    for (const [name, meta] of statusLookup) {
      const existing = byLabel.get(meta.label);
      if (existing) {
        existing.names.push(name);
      } else {
        byLabel.set(meta.label, { names: [name], color: meta.color });
      }
    }
    return Array.from(byLabel, ([label, { names, color }]) => ({
      names,
      label,
      color,
    }));
  }, [statusLookup]);

  const columns = useMemo(
    () =>
      getDossierColumns(
        layout.columns,
        statusLookup,
        statusesPending,
        layout.columnLabels,
      ),
    [layout.columns, layout.columnLabels, statusLookup, statusesPending],
  );
  // The grid templates depend on which columns actually render, so they are
  // computed once here and applied identically to the header row, the skeleton
  // rows and the data rows (see `ROW_GRID` in dossierColumns.tsx).
  const rowGridStyle = useMemo(() => getRowGridStyle(columns), [columns]);
  // One skeleton row per row the loaded page will have (rather than an
  // arbitrary smaller count), so the skeleton phase occupies exactly the height
  // the loaded page will: the entrance animation replaces skeleton rows with
  // real ones 1:1, instead of the card visibly growing once data arrives.
  const skeletonRowKeys = useMemo(
    () =>
      Array.from(
        { length: itemsPerPage },
        (_, index) => `dossier-skeleton-${index}`,
      ),
    [itemsPerPage],
  );

  const goToPage = (next: number): void => {
    void navigate({ search: (prev) => ({ ...prev, page: next }) });
  };

  const toggleUnread = (): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, unread: !prev.unread }),
    });
  };

  const toggleActionRequired = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        actionRequired: !prev.actionRequired,
      }),
    });
  };

  const toggleArchived = (): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, archived: !prev.archived }),
    });
  };

  // The "Ongelezen" tile is a shortcut to "my active unread work": it always
  // brings you back to the active scope in addition to toggling the unread
  // filter, mirroring the Ledger concept's KPI tiles. Like "Totaal", it also
  // resets the Period, Hersteller, Opdrachtgever and Toegewezen aan filters —
  // both tiles are "jump to this scope" shortcuts. The "Ongelezen" pill in
  // the filter bar below toggles the same filter without forcing the scope
  // or clearing those filters, so it stays freely combinable with
  // "Gearchiveerd" and them.
  const focusUnread = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        archived: false,
        unread: !prev.unread,
        period: "all",
        servicePartnerId: undefined,
        companyId: undefined,
        assigneeId: undefined,
        status: [],
      }),
    });
  };

  // Same "jump to this scope" shortcut as "Ongelezen", for the "Actie
  // vereist" tile (see DOSSIER_OVERVIEW_KPIS): resets the same set of
  // filters and toggles `actionRequired` instead of `unread`. The two are
  // mutually exclusive per affiliate (a `view_configuration` shows one or
  // the other, never both, see the `check` client fixture), so there is no
  // need to also reset `unread` here.
  const focusActionRequired = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        archived: false,
        actionRequired: !prev.actionRequired,
        period: "all",
        servicePartnerId: undefined,
        companyId: undefined,
        assigneeId: undefined,
        status: [],
      }),
    });
  };

  // The "Mijn dossiers" tile is a shortcut to "my active work": it always
  // brings you back to the active scope, like Totaal and Ongelezen, and
  // resets the Period, Hersteller, Opdrachtgever and Ongelezen filters the
  // same way those two tiles do — all three tiles are "jump to this scope"
  // shortcuts, so they share the same reset behaviour. It toggles the
  // Toegewezen aan filter between "me" and "everyone", so clicking the tile
  // again clears it, mirroring the Ledger concept.
  const focusMine = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        archived: false,
        unread: false,
        actionRequired: false,
        period: "all",
        servicePartnerId: undefined,
        companyId: undefined,
        assigneeId:
          prev.assigneeId === currentUser.userId
            ? undefined
            : currentUser.userId,
        status: [],
      }),
    });
  };

  const setPeriod = (next: Period): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, period: next }),
    });
  };

  const setServicePartner = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, servicePartnerId: next }),
    });
  };

  const setCompany = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, companyId: next }),
    });
  };

  const setAssignee = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, assigneeId: next }),
    });
  };

  const setUserCollection = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, userCollectionId: next }),
    });
  };

  const setExternalInsured = (next: boolean | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, externalInsured: next }),
    });
  };

  const setClaimable = (next: boolean | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, claimable: next }),
    });
  };

  const setBrand = (next: readonly string[]): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, brand: [...next] }),
    });
  };

  const setEndOfContract = (next: EndOfContract | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, endOfContract: next }),
    });
  };

  const setCauseOfDamage = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, causeOfDamage: next }),
    });
  };

  const setDossierType = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, dossierType: next }),
    });
  };

  const setRepairType = (next: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...prev, page: 1, repairType: next }),
    });
  };

  // Clicking the already-active column reverses its direction (one active
  // column, no third "unsorted" state); clicking a different column starts
  // it at that column's own default direction (see
  // `DEFAULT_DIRECTION_FOR_FIELD`). Resets `page` to 1 like every other
  // filter/sort change above; every other search param is preserved by the
  // spread, including the active filters.
  const toggleSort = (field: DossierSortField): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        sortBy: field,
        sortDirection: nextSortDirection(
          effectiveSortBy,
          effectiveSortDirection,
          field,
        ),
      }),
    });
  };

  // Multi-select: toggling a status adds or removes it from the set rather
  // than replacing it, so several statuses can be combined (OR semantics,
  // matching the backend's `status IN (...)` filter). A merged chip (see
  // `statusOptions`) carries every underlying name sharing its label and
  // toggles all of them together: activating adds whichever are missing,
  // deactivating removes all of them.
  const toggleStatus = (names: readonly string[]): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        status: names.every((name) => prev.status.includes(name))
          ? prev.status.filter((value) => !names.includes(value))
          : [...new Set([...prev.status, ...names])],
      }),
    });
  };

  const resetFilters = (): void => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        archived: false,
        unread: false,
        actionRequired: false,
        period: "all",
        servicePartnerId: undefined,
        companyId: undefined,
        assigneeId: undefined,
        userCollectionId: undefined,
        status: [],
        externalInsured: undefined,
        claimable: undefined,
        brand: [],
        endOfContract: undefined,
        causeOfDamage: undefined,
        dossierType: undefined,
        repairType: undefined,
      }),
    });
  };

  const otherFiltersActive =
    unread ||
    actionRequired ||
    period !== "all" ||
    servicePartnerId !== undefined ||
    companyId !== undefined ||
    assigneeId !== undefined ||
    userCollectionId !== undefined ||
    status.length > 0 ||
    // Three-state: `false` is an active filter too, so this cannot be a
    // truthiness check.
    externalInsured !== undefined ||
    claimable !== undefined ||
    brand.length > 0 ||
    endOfContract !== undefined ||
    causeOfDamage !== undefined ||
    dossierType !== undefined ||
    repairType !== undefined;
  const filtersActive = archived || otherFiltersActive;
  // Includes `period === "all"` so the tile's selection indicator stays
  // aligned with the actual list scope: the KPI queries above are never
  // period- or hersteller-scoped, so "Totaal" must not read as selected while
  // a period filter is narrowing the list. Hersteller and Opdrachtgever are
  // excluded from this check on purpose — they're orthogonal refinements
  // (e.g. "Ongelezen in Rotterdam" is a sensible combination), so neither
  // should make either tile read as deselected. Toegewezen aan (`assigneeId`)
  // is excluded from that exemption: it's the exact field "Mijn dossiers"
  // owns, so "Totaal" and "Mijn dossiers" must be mutually exclusive.
  const isTotalSelected =
    !archived &&
    !unread &&
    !actionRequired &&
    period === "all" &&
    assigneeId === undefined;
  const isUnreadSelected = unread && !archived;
  const isActionRequiredSelected = actionRequired && !archived;
  const isMineSelected = !archived && assigneeId === currentUser.userId;

  // The tiles are uniform, so which of them render (and in what order) is a
  // straight lookup on the configured list rather than three hardcoded blocks.
  // Label and icon are shared with the dashboard's own tile set (see
  // DOSSIER_KPI_TILE_META, both pages render the same possible tiles off the
  // same stats); only the per-tile click handler and selected state are
  // specific to this list's own filter state.
  const kpiTiles: Record<
    DossierOverviewKpi,
    {
      readonly label: string;
      readonly icon: typeof FolderOpen;
      readonly value: number;
      readonly onClick: () => void;
      readonly selected: boolean;
    }
  > = {
    total: {
      ...DOSSIER_KPI_TILE_META.total,
      value: DOSSIER_KPI_TILE_META.total.value(statsQuery.data),
      onClick: resetFilters,
      selected: isTotalSelected,
    },
    unread: {
      ...DOSSIER_KPI_TILE_META.unread,
      value: DOSSIER_KPI_TILE_META.unread.value(statsQuery.data),
      onClick: focusUnread,
      selected: isUnreadSelected,
    },
    actionRequired: {
      ...DOSSIER_KPI_TILE_META.actionRequired,
      value: DOSSIER_KPI_TILE_META.actionRequired.value(statsQuery.data),
      onClick: focusActionRequired,
      selected: isActionRequiredSelected,
    },
    mine: {
      ...DOSSIER_KPI_TILE_META.mine,
      value: DOSSIER_KPI_TILE_META.mine.value(statsQuery.data),
      onClick: focusMine,
      selected: isMineSelected,
    },
  };

  const totalItems = data?.totalItems ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dossiers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Overzicht van de actuele werkvoorraad.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isPending ? (
            // Reserves the trigger's own footprint (Button size="sm"), so the
            // heading row does not shift once the count is known.
            <Skeleton aria-hidden="true" className="h-9 w-36 rounded-xl" />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button size="sm" variant="secondary">
                  <Download className="size-4" aria-hidden="true" />
                  {mayImportAccidentReports ? "Import / export" : "Exporteren"}
                  <DropdownMenuChevron />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-w-80">
                {mayImportAccidentReports && (
                  <DropdownMenuItem
                    className="flex-col items-start gap-1"
                    onSelect={() => setAccidentReportImportOpen(true)}
                  >
                    <span className="font-medium">
                      Schadeformulieren bijwerken
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Registreer aanwezige formulieren vanuit het Excel-bestand
                      van Arval.
                    </span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="flex-col items-start gap-1"
                  disabled={totalItems === 0 || exportMutation.isPending}
                  onSelect={() =>
                    exportMutation.mutate(listParams, {
                      onSuccess: () =>
                        toast.success(
                          "Export wordt gegenereerd. Je ontvangt een e-mail zodra de download klaarstaat.",
                        ),
                      onError: () =>
                        toast.error("Exporteren mislukt. Probeer het opnieuw."),
                    })
                  }
                >
                  <span className="font-medium">Export genereren</span>
                  <span className="text-xs text-muted-foreground">
                    {totalItems === 0
                      ? "Er zijn geen dossiers om te exporteren."
                      : `De export bevat ${totalItems} gefilterde dossiers. Je ontvangt de downloadlink per e-mail.`}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {mayImportAccidentReports && (
            <AccidentReportImportDialog
              open={accidentReportImportOpen}
              onOpenChange={setAccidentReportImportOpen}
            />
          )}

          {/* Entry stays available while the overview loads. */}
          {mayCreateDossier && (
            <Link
              to="/dossiers/new"
              search={{}}
              className={buttonVariants({ size: "sm" })}
            >
              Nieuw dossier
            </Link>
          )}
        </div>
      </div>

      {/* Nothing configuration-derived is painted before the configuration is
          known: rendering the fallback layout meanwhile would flash tiles,
          filters and columns the affiliate did not configure (and the user may
          not be allowed to see) until the request lands. A configuration that
          fails to load never gets to paint them either — the page shows the
          same recoverable error the list uses instead. */}
      {layoutQuery.isPending && <DossierOverviewSkeleton />}
      {layoutQuery.isError && (
        <Card className="overflow-hidden">
          <div role="alert" className="space-y-3 px-4 py-10 text-center">
            <p className="text-muted-foreground">
              We konden de dossiers niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={retryList}>
              Opnieuw proberen
            </Button>
          </div>
        </Card>
      )}

      {/* Every tile doubles as a one-tap filter (StatCard's interactive
          variant): Totaal resets every filter, Ongelezen jumps to the
          active+unread view, Mijn dossiers jumps to the active view scoped
          to the current user. Which tiles appear, and in what order, is
          affiliate configuration. */}
      {isLayoutResolved && layout.kpis.length > 0 && (
        <m.div
          role="group"
          aria-label="Kerncijfers"
          variants={staggerContainer}
          initial={motionInitial}
          animate="visible"
          className="grid gap-3 sm:grid-cols-3"
        >
          {layout.kpis.map((kpi) => {
            const tile = kpiTiles[kpi];
            return (
              <m.div key={kpi} variants={staggerItem}>
                <StatCard
                  label={tile.label}
                  icon={tile.icon}
                  value={
                    isStatsPending ? "–" : <AnimatedNumber value={tile.value} />
                  }
                  onClick={tile.onClick}
                  selected={tile.selected}
                  className="w-full"
                />
              </m.div>
            );
          })}
        </m.div>
      )}

      {/* The bar disappears entirely when the affiliate configured no filters,
          rather than rendering an empty frame. */}
      {isLayoutResolved && layout.filters.length > 0 && (
        <fieldset className="rounded-2xl border border-border/70 bg-surface p-4 shadow-xs">
          <legend className="sr-only">Filter dossiers</legend>
          <div className="flex flex-wrap items-center gap-2">
            {enabledFilters.has("unread") && (
              <FilterToggle active={unread} onClick={toggleUnread}>
                <StatusDot tone="info" />
                Ongelezen
              </FilterToggle>
            )}
            {enabledFilters.has("actionRequired") && (
              <FilterToggle
                active={actionRequired}
                onClick={toggleActionRequired}
              >
                <StatusDot tone="warning" />
                Actie vereist
              </FilterToggle>
            )}
            {enabledFilters.has("archived") && (
              <FilterToggle active={archived} onClick={toggleArchived}>
                <StatusDot tone="neutral" />
                Gearchiveerd
              </FilterToggle>
            )}

            <div className="ms-auto flex flex-wrap items-center gap-2">
              {enabledFilters.has("company") &&
                (companiesQuery.isError ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>Opdrachtgevers konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={() => {
                        void companiesQuery.refetch();
                      }}
                      className="font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Opnieuw proberen
                    </button>
                  </div>
                ) : (
                  <Combobox
                    aria-label="Filter op opdrachtgever"
                    placeholder="Alle opdrachtgevers"
                    searchPlaceholder="Zoek een opdrachtgever..."
                    value={companyId}
                    onValueChange={setCompany}
                    options={companyOptions}
                    loading={companiesQuery.isPending}
                    size="sm"
                  />
                ))}
              {enabledFilters.has("assignee") &&
                (usersQuery.isError ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>Behandelaars konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={() => {
                        void usersQuery.refetch();
                      }}
                      className="font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Opnieuw proberen
                    </button>
                  </div>
                ) : (
                  <Combobox
                    aria-label="Filter op behandelaar"
                    placeholder="Alle behandelaars"
                    searchPlaceholder="Zoek een behandelaar..."
                    value={assigneeId}
                    onValueChange={setAssignee}
                    options={assigneeOptions}
                    loading={usersQuery.isPending}
                    size="sm"
                  />
                ))}
              {enabledFilters.has("userCollection") &&
                (teamsQuery.isError ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>Teams konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={() => {
                        void teamsQuery.refetch();
                      }}
                      className="font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Opnieuw proberen
                    </button>
                  </div>
                ) : (
                  <Combobox
                    aria-label="Filter op team"
                    placeholder="Alle teams"
                    searchPlaceholder="Zoek een team..."
                    value={userCollectionId}
                    onValueChange={setUserCollection}
                    options={teamOptions}
                    loading={teamsQuery.isPending}
                    size="sm"
                  />
                ))}
              {enabledFilters.has("servicePartner") &&
                (servicePartnersQuery.isError ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>Herstellers konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={() => {
                        void servicePartnersQuery.refetch();
                      }}
                      className="font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Opnieuw proberen
                    </button>
                  </div>
                ) : (
                  <Combobox
                    aria-label="Filter op hersteller"
                    placeholder="Alle herstellers"
                    searchPlaceholder="Zoek een hersteller..."
                    value={servicePartnerId}
                    onValueChange={setServicePartner}
                    options={servicePartnerOptions}
                    loading={servicePartnersQuery.isPending}
                    size="sm"
                  />
                ))}
              {enabledFilters.has("period") && (
                <Combobox
                  aria-label="Filter op periode"
                  placeholder="Alle periodes"
                  searchable={false}
                  size="sm"
                  value={period === "all" ? undefined : period}
                  onValueChange={(next) => setPeriod((next as Period) ?? "all")}
                  options={PERIOD_OPTIONS}
                />
              )}
              {/* The three report filters (RK-1230). The two booleans are
                  three-state fixed-option Comboboxes: "Alle" is the empty
                  value (no parameter sent), while both other options send a
                  real boolean — including `false`, which is a deliberate
                  choice, not "no filter". "Alle" is not an option in the
                  list: the two choices do not partition the collection (a
                  dossier with no fleet record matches neither side), so no
                  label may suggest they are exhaustive. */}
              {enabledFilters.has("externalInsured") && (
                <Combobox
                  aria-label="Filter op verzekering"
                  placeholder="Verzekering"
                  searchable={false}
                  size="sm"
                  value={booleanToOptionValue(externalInsured)}
                  onValueChange={(next) =>
                    setExternalInsured(optionValueToBoolean(next))
                  }
                  options={EXTERNAL_INSURED_OPTIONS}
                />
              )}
              {enabledFilters.has("claimable") && (
                <Combobox
                  aria-label="Filter op verhaalbaarheid"
                  placeholder="Verhaalbaarheid"
                  searchable={false}
                  size="sm"
                  value={booleanToOptionValue(claimable)}
                  onValueChange={(next) =>
                    setClaimable(optionValueToBoolean(next))
                  }
                  options={CLAIMABLE_OPTIONS}
                />
              )}
              {enabledFilters.has("brand") &&
                (vehicleBrandsQuery.isError ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>Merken konden niet worden geladen.</span>
                    <button
                      type="button"
                      onClick={() => {
                        void vehicleBrandsQuery.refetch();
                      }}
                      className="font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Opnieuw proberen
                    </button>
                  </div>
                ) : (
                  <MultiSelect
                    aria-label="Filter op merk"
                    placeholder="Alle merken"
                    searchPlaceholder="Zoek een merk..."
                    size="sm"
                    values={brand}
                    onValuesChange={setBrand}
                    options={brandOptions}
                    loading={vehicleBrandsQuery.isPending}
                  />
                ))}
              {enabledFilters.has("matchedRule") &&
                (layout.filterOptions?.matchedRule?.ruleIds.length ?? 0) >
                  0 && (
                  <Combobox
                    aria-label={END_OF_CONTRACT_LABEL}
                    placeholder={END_OF_CONTRACT_LABEL}
                    searchable={false}
                    clearable={false}
                    size="sm"
                    value={endOfContract ?? "all"}
                    triggerLabel={`${END_OF_CONTRACT_LABEL}: ${endOfContract === "yes" ? "Ja" : endOfContract === "no" ? "Nee" : "Alle"}`}
                    onValueChange={(next) =>
                      setEndOfContract(toEndOfContract(next))
                    }
                    options={END_OF_CONTRACT_OPTIONS}
                  />
                )}
              {enabledFilters.has("causeOfDamage") && (
                <Combobox
                  aria-label="Filter op schadeoorzaak"
                  placeholder="Schadeoorzaak"
                  searchPlaceholder="Zoek een schadeoorzaak..."
                  size="sm"
                  value={causeOfDamage ?? ""}
                  onValueChange={(next) => setCauseOfDamage(next || undefined)}
                  options={causeOfDamageOptions}
                  loading={causesOfDamageQuery.isPending}
                />
              )}
              {enabledFilters.has("dossierType") && (
                <Combobox
                  aria-label="Filter op type"
                  placeholder="Type"
                  searchable={false}
                  size="sm"
                  value={dossierType ?? ""}
                  onValueChange={(next) => setDossierType(next || undefined)}
                  options={DOSSIER_TYPE_OPTIONS}
                />
              )}
              {enabledFilters.has("repairType") && (
                <Combobox
                  aria-label="Filter op herstelmethode"
                  placeholder="Herstelmethode"
                  searchable={false}
                  size="sm"
                  value={repairType ?? ""}
                  onValueChange={(next) => setRepairType(next || undefined)}
                  options={REPAIR_TYPE_OPTIONS}
                />
              )}
              {filtersActive && (
                // A Combobox with a value renders its own icon-only clear
                // button, also named "Wissen": in the same bar, two controls
                // with that name are indistinguishable to a screen reader, so
                // this one spells out that it clears every filter. The visible
                // text stays inside the accessible name (WCAG 2.5.3).
                <button
                  type="button"
                  aria-label="Alle filters wissen"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                >
                  <X className="size-3.5" />
                  Wissen
                </button>
              )}
            </div>
          </div>
          {/* Status is a multi-select chip row (not a Combobox, which only
            supports a single value) on its own line, matching the Ledger
            concept. Options come from /admin/dossier-statuses/brief
            (statusOptions, deduplicated across dossier subtypes), each
            rendered as a toggleable pill with a tone-colored dot, so the
            available statuses and their color always match what the Status
            column shows for the same dossier. The same lookup also backs the
            Status column, so it is fetched whether or not this filter is
            configured. */}
          {enabledFilters.has("status") &&
            (statusesErrored ? (
              <div
                role="alert"
                className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3 text-sm text-muted-foreground"
              >
                <span>Statussen konden niet worden geladen.</span>
                <button
                  type="button"
                  onClick={() => {
                    refetchStatuses();
                  }}
                  className="font-medium text-primary transition-colors hover:text-primary/80"
                >
                  Opnieuw proberen
                </button>
              </div>
            ) : (
              (statusesPending || statusOptions.length > 0) && (
                <div className="mt-3 flex min-h-10 flex-wrap items-center gap-1.5 border-t border-border/60 pt-3">
                  <span className="mr-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Status
                  </span>
                  {statusesPending ? (
                    <>
                      <Skeleton className="h-6 w-14 rounded-full" />
                      <Skeleton className="h-6 w-20 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                      <Skeleton className="h-6 w-24 rounded-full" />
                    </>
                  ) : (
                    statusOptions.map((option) => {
                      // A merged chip (see `statusOptions`) can be partially
                      // selected when only some of its underlying names are
                      // active (e.g. the Check name but not the Greenwheels
                      // one) — `aria-pressed="mixed"` reports that state
                      // accurately, and the tinted style still applies so
                      // the chip visibly reads as "on" either way, matching
                      // `toggleStatus`'s own all-or-nothing click behaviour
                      // (a `some`-based active/pressed state would make a
                      // partially-selected chip look identical to a fully
                      // selected one, yet clicking it adds names instead of
                      // removing them).
                      const allSelected = option.names.every((name) =>
                        status.includes(name),
                      );
                      const someSelected = option.names.some((name) =>
                        status.includes(name),
                      );
                      return (
                        <button
                          key={option.names.join(",")}
                          type="button"
                          onClick={() => toggleStatus(option.names)}
                          aria-pressed={
                            allSelected ? true : someSelected ? "mixed" : false
                          }
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                            someSelected
                              ? "border-primary/30 bg-primary-soft text-primary"
                              : "border-border/60 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                          )}
                        >
                          <StatusDot tone={option.color} className="size-1.5" />
                          {option.label}
                        </button>
                      );
                    })
                  )}
                </div>
              )
            ))}
        </fieldset>
      )}

      {/* role="table" + the row/columnheader/cell roles below restore the
          semantics a screen reader needs to navigate this list by row and
          column — a real <table> isn't used (see ROW_GRID in
          dossierColumns.tsx: a fixed CSS grid, not a table, is what fixed the
          row-entrance-animation resize bug), so ARIA roles stand in for the
          table semantics the DOM would otherwise imply for free. */}
      {isLayoutResolved && hasNoColumns && (
        <Card className="px-4 py-10 text-center text-muted-foreground">
          Er zijn geen kolommen ingesteld die je mag zien.
        </Card>
      )}

      {isLayoutResolved && !hasNoColumns && (
        <Card className="overflow-hidden" role="table" aria-label="Dossiers">
          {/* Header row/columnheaders mirror a <table>'s <thead>. */}
          {/* biome-ignore lint/a11y/useSemanticElements: role="rowgroup" stands in for <thead>/<tbody> — see the table-vs-grid tradeoff explained above role="table". */}
          <div role="rowgroup">
            {/* biome-ignore lint/a11y/useFocusableInteractive: role="row" is the static WAI-ARIA table pattern (not role="grid"), whose rows are never expected to be focusable — see the note above role="table". */}
            {/* biome-ignore lint/a11y/useSemanticElements: a real <tr> can't be used here without reopening the table-vs-grid tradeoff explained above role="table" — overriding a table element's display to grid strips its implicit semantics, so it would need this exact role anyway. */}
            <div
              role="row"
              style={rowGridStyle}
              className={cn(
                ROW_GRID,
                "border-b border-border/70 bg-surface-muted/50 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground",
              )}
            >
              {columns.map((column) => {
                // Not every column that *can* carry a `sortBy` (see
                // dossierColumns.tsx) is one this affiliate actually made
                // clickable — that is `layout.sorting.columns`, which is
                // also already filtered to columns the current user may see
                // (see the server/client resolvers in
                // dossierOverviewLayout.ts).
                if (
                  column.sortBy === undefined ||
                  !layout.sorting?.columns.includes(column.sortBy)
                ) {
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: role="columnheader" stands in for <th scope="col"> — see the table-vs-grid tradeoff explained above role="table".
                    // biome-ignore lint/a11y/useFocusableInteractive: a columnheader in the static WAI-ARIA table pattern (not role="grid") is never expected to be focusable — see the note above role="table".
                    <span
                      key={column.id}
                      role="columnheader"
                      className={column.visibilityClassName}
                    >
                      {column.header}
                    </span>
                  );
                }

                const sortField = column.sortBy;
                const isActive = effectiveSortBy === sortField;
                const direction: "asc" | "desc" | null =
                  isActive && effectiveSortDirection
                    ? effectiveSortDirection === "ASC"
                      ? "asc"
                      : "desc"
                    : null;
                const upcoming = nextSortDirection(
                  effectiveSortBy,
                  effectiveSortDirection,
                  sortField,
                );
                const accessibleLabel = `Sorteer op ${column.header} ${
                  upcoming === "ASC" ? "oplopend" : "aflopend"
                }`;

                return (
                  // biome-ignore lint/a11y/useSemanticElements: role="columnheader" stands in for <th scope="col"> — see the table-vs-grid tradeoff explained above role="table".
                  // biome-ignore lint/a11y/useFocusableInteractive: the columnheader itself still isn't focusable (only the real <button> inside it is) — see the note above role="table".
                  <span
                    key={column.id}
                    role="columnheader"
                    aria-sort={
                      isActive
                        ? direction === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={column.visibilityClassName}
                  >
                    <SortableColumnHeader
                      direction={direction}
                      accessibleLabel={accessibleLabel}
                      onSort={() => toggleSort(sortField)}
                    >
                      {column.header}
                    </SortableColumnHeader>
                  </span>
                );
              })}
              {/* biome-ignore lint/a11y/useSemanticElements: role="columnheader" stands in for <th scope="col"> — see the table-vs-grid tradeoff explained above role="table". */}
              {/* biome-ignore lint/a11y/useFocusableInteractive: a columnheader in the static WAI-ARIA table pattern (not role="grid") is never expected to be focusable — see the note above role="table". */}
              <span role="columnheader">
                <span className="sr-only">Openen</span>
              </span>
            </div>
          </div>

          {/* The row/cell roles below mirror a <table>'s <tbody>. The two stable
            wrappers preserve table structure and DOM identity across
            pagination; newly mounted rows own their staggered entrance, so
            the body itself never has to remount to replay it. */}
          {/* biome-ignore lint/a11y/useSemanticElements: role="rowgroup" stands in for <thead>/<tbody> — see the table-vs-grid tradeoff explained above role="table". */}
          <div role="rowgroup">
            <div
              // The dim (below) is a purely visual "stale, refreshing" cue;
              // aria-busy carries the same signal to assistive tech, since the
              // dim itself is invisible to a screen reader.
              aria-busy={isPlaceholderData}
              className={cn(
                "text-sm transition-opacity",
                isPlaceholderData && "opacity-60",
              )}
            >
              <DossiersRows
                columns={columns}
                rowGridStyle={rowGridStyle}
                skeletonRowKeys={skeletonRowKeys}
                emptyMessage={
                  archived && !otherFiltersActive
                    ? "Nog geen gearchiveerde dossiers."
                    : "Geen dossiers gevonden voor deze filters."
                }
                isPending={isPending}
                isError={isError}
                members={data?.member}
                onRetry={retryList}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Pagination sits outside the card — Ledger concept pattern. */}
      {isLayoutResolved &&
        !hasNoColumns &&
        (isPending || isError || totalItems > 0) && (
          <Pagination
            page={page}
            pageSize={itemsPerPage}
            totalItems={totalItems}
            status={isPending ? "pending" : isError ? "error" : "ready"}
            onPageChange={goToPage}
          />
        )}
    </div>
  );
}
